import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { profileAPI } from '../lib/api';
import { 
    Users, 
    Search, 
    MapPin, 
    Briefcase,
    Building2,
    Lock,
    LogIn,
    Shield
} from 'lucide-react';
import { Badge } from '../components/ui/badge';

const MembersPage = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated, loading: authLoading, login } = useAuth();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            setLoading(false);
            return;
        }

        if (isAuthenticated) {
            fetchMembers();
        }
    }, [isAuthenticated, authLoading]);

    const fetchMembers = async () => {
        try {
            const response = await profileAPI.getMembers({ limit: 100 });
            setMembers(response.data.members || []);
        } catch (error) {
            console.error('Error fetching members:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredMembers = members.filter(member => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            member.name?.toLowerCase().includes(query) ||
            member.organization?.toLowerCase().includes(query) ||
            member.title?.toLowerCase().includes(query) ||
            member.city?.toLowerCase().includes(query) ||
            member.state?.toLowerCase().includes(query)
        );
    });

    // Show login required screen if not authenticated
    if (!authLoading && !isAuthenticated) {
        return (
            <div className="min-h-screen bg-background" data-testid="members-page-login-required">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <Card className="text-center">
                        <CardContent className="pt-12 pb-10">
                            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Lock className="h-10 w-10 text-primary" />
                            </div>
                            <h1 className="font-heading text-3xl font-bold mb-4">
                                Members Only
                            </h1>
                            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                                The Member Directory is exclusively available to HAAPE members. 
                                Please log in with your member account to connect with other members.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <Button onClick={login} size="lg" className="gap-2" data-testid="login-btn">
                                    <LogIn className="h-4 w-4" />
                                    Member Login
                                </Button>
                                <Link to="/apply">
                                    <Button variant="outline" size="lg" data-testid="apply-btn">
                                        Apply For Membership
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="members-page">
            {/* Header */}
            <div className="bg-card border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="font-heading text-3xl font-bold mb-2 flex items-center gap-3">
                                <Users className="h-8 w-8 text-primary" />
                                Member Directory
                            </h1>
                            <p className="text-muted-foreground">
                                Connect with other HAAPE members
                            </p>
                        </div>
                        <div className="relative max-w-md w-full">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search members..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10"
                                data-testid="search-members"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {loading ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[...Array(6)].map((_, i) => (
                            <Card key={i}>
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-4">
                                        <Skeleton className="h-16 w-16 rounded-full" />
                                        <div className="flex-1">
                                            <Skeleton className="h-5 w-32 mb-2" />
                                            <Skeleton className="h-4 w-24" />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : filteredMembers.length === 0 ? (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="font-heading text-xl font-semibold mb-2">No Members Found</h3>
                            <p className="text-muted-foreground">
                                {searchQuery 
                                    ? 'No members match your search criteria' 
                                    : 'No members have completed their profiles yet'}
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredMembers.map((member) => (
                            <Link 
                                key={member.user_id} 
                                to={`/members/${member.user_id}`}
                                className="group"
                            >
                                <Card className="h-full hover:border-primary/50 hover:shadow-md transition-all duration-200">
                                    <CardContent className="p-6">
                                        <div className="flex items-start gap-4">
                                            <Avatar className="h-16 w-16 shrink-0">
                                                <AvatarImage src={member.picture} alt={member.name} />
                                                <AvatarFallback className="text-xl">
                                                    {member.name?.charAt(0) || 'M'}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h3 className="font-heading font-semibold truncate group-hover:text-primary transition-colors">
                                                        {member.name}
                                                    </h3>
                                                    {member.role === 'chapter_director' && (
                                                        <Badge className="text-[10px] px-1.5 py-0 bg-amber-500 hover:bg-amber-600 text-white shrink-0" data-testid={`cd-badge-${member.user_id}`}>
                                                            <Shield className="h-2.5 w-2.5 mr-0.5" />
                                                            Chapter Director
                                                        </Badge>
                                                    )}
                                                </div>
                                                {member.title && (
                                                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                                        <Briefcase className="h-3 w-3 shrink-0" />
                                                        <span className="truncate">{member.title}</span>
                                                    </p>
                                                )}
                                                {member.organization && (
                                                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                                        <Building2 className="h-3 w-3 shrink-0" />
                                                        <span className="truncate">{member.organization}</span>
                                                    </p>
                                                )}
                                                {(member.city || member.state) && (
                                                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                                        <MapPin className="h-3 w-3 shrink-0" />
                                                        <span className="truncate">
                                                            {[member.city, member.state].filter(Boolean).join(', ')}
                                                        </span>
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MembersPage;
